import type { ApiError, AuthUser, LoginResponse } from '@lms/shared-types';
import { useAuthStore } from '@/stores/authStore';
import { withCsrfHeaders } from '@/lib/api-headers';

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: ApiError,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  auth?: boolean;
  _retry?: boolean;
};

async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const data = text ? (JSON.parse(text) as T) : ({} as T);

  if (!res.ok) {
    const err = data as ApiError;
    const message =
      res.status === 403 && !err.code ? 'Sin acceso' : (err.message ?? res.statusText);
    throw new ApiRequestError(res.status, message, err);
  }

  return data;
}

let refreshPromise: Promise<boolean> | null = null;

function requestFailed(error: unknown): never {
  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    if (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('load failed')
    ) {
      throw new ApiRequestError(0, 'No se pudo conectar con el servidor');
    }
  }
  throw error;
}

export async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: withCsrfHeaders(),
    })
      .then(async (res) => {
        const data = (await res.json()) as { user?: AuthUser | null };
        if (!res.ok || !data.user) {
          useAuthStore.getState().clearSession();
          return false;
        }
        useAuthStore.getState().setUser(data.user);
        useAuthStore.getState().markAuthenticated();
        return true;
      })
      .catch(() => {
        useAuthStore.getState().clearSession();
        return false;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function clearSessionAndRedirect() {
  useAuthStore.getState().clearSession();
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = true, headers, _retry, ...init } = options;

  const url = path.startsWith('/api') ? path : `/api${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      credentials: 'include',
      headers: withCsrfHeaders({
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      }),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    requestFailed(error);
  }

  if (res.status === 401 && auth && !_retry) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return api<T>(path, { ...options, _retry: true });
    }
    clearSessionAndRedirect();
  }

  return parseResponse<T>(res);
}

export async function apiFormData<T>(
  path: string,
  formData: FormData,
  options: Omit<RequestOptions, 'body'> = {},
): Promise<T> {
  const { auth = true, headers, _retry, method = 'POST', ...init } = options;

  const url = path.startsWith('/api') ? path : `/api${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      method,
      credentials: 'include',
      headers: withCsrfHeaders({
        ...headers,
      }),
      body: formData,
    });
  } catch (error) {
    requestFailed(error);
  }

  if (res.status === 401 && auth && !_retry) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return apiFormData<T>(path, formData, { ...options, _retry: true });
    }
    clearSessionAndRedirect();
  }

  return parseResponse<T>(res);
}

export const authApi = {
  login: (email: string, password: string) =>
    api<LoginResponse>('/auth/login', { method: 'POST', body: { email, password }, auth: false }),

  register: (data: { name: string; email: string; password: string; acceptedLegalPolicyIds?: string[] }) =>
    api<LoginResponse>('/auth/register', { method: 'POST', body: data, auth: false }),

  logout: () => api<{ message: string }>('/auth/logout', { method: 'POST' }),

  me: () => api<{ user: AuthUser }>('/auth/me'),

  refresh: () =>
    api<{ user: AuthUser | null }>('/auth/refresh', {
      method: 'POST',
      auth: false,
    }),
};

export function listResource<T>(endpoint: string, params?: Record<string, string | number>) {
  const qs = params
    ? `?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]))}`
    : '';
  return api<{ data: T[]; meta?: { total: number; page: number; perPage: number; lastPage?: number } } | T[]>(
    `${endpoint}${qs}`,
  ).then((res) => (Array.isArray(res) ? { data: res } : res));
}

export async function listAllResource<T>(endpoint: string, params?: Record<string, string | number>) {
  const perPage = 100;
  const first = await listResource<T>(endpoint, { ...params, page: 1, perPage });
  const rows = [...(first.data ?? [])];
  const lastPage = first.meta?.lastPage ?? 1;
  for (let page = 2; page <= lastPage; page += 1) {
    const next = await listResource<T>(endpoint, { ...params, page, perPage });
    rows.push(...(next.data ?? []));
  }
  return { data: rows, meta: first.meta };
}

export async function apiDownload(path: string, filename?: string): Promise<void> {
  const url = path.startsWith('/api') ? path : `/api${path}`;
  let res = await fetch(url, { credentials: 'include', headers: withCsrfHeaders() });
  if (res.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed) {
      res = await fetch(url, { credentials: 'include', headers: withCsrfHeaders() });
    }
  }
  if (!res.ok) {
    const text = await res.text();
    let message = res.statusText;
    try {
      message = (JSON.parse(text) as ApiError).message ?? message;
    } catch {
      /* ignore */
    }
    throw new ApiRequestError(res.status, message);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const name = filename ?? match?.[1] ?? 'download';
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
}
